import {useCallback, useLayoutEffect, useRef} from 'react'
import {type PDFDocumentProxy} from 'pdfjs-dist'

import {
  type PageAxis,
  type PageDrag,
  readPageDrag,
  readPageTouch,
} from '#/lib/document/pageTouch'
import {fitPagedPage} from '#/lib/document/pdfFit'
import {PDF_SCROLL_AREA_CLASS} from '#/lib/document/pdfStyles.web'
import {dragOffset, edgeSlack, readSnap} from '#/lib/document/snapPager'
import {useNonReactiveCallback} from '#/lib/hooks/useNonReactiveCallback'
import {LazyPage} from './LazyPage.web'

/**
 * One wheel gesture, one page, with the two numbers the document card's own pager
 * uses for the same job — so a wheel means the same thing in both surfaces.
 */
const WHEEL_COOLDOWN_MS = 420
const WHEEL_MIN_DELTA = 14

/**
 * How long the paper takes to come back from a flick — the document card's own
 * ease-out, on the same move, so one gesture feels like one thing in both surfaces.
 */
const FLICK_EASE_MS = 160

/**
 * The air a page keeps inside its own row once it is larger than the row.
 *
 * At `zoom > 1` the page no longer fits the surface it is read in, so the row grows
 * to hold it whole: the reader is inspecting a line, and a page clipped to the shape
 * of the window would be a page they cannot see the end of.
 */
const ZOOMED_ROW_AIR = 24

/**
 * The reader's page-by-page view: the document as a run of pages, one to a screenful,
 * dragged rather than swapped — down a column or across a row, the reader's own choice.
 *
 * A page used to *be* the view: the reader pressed an arrow, or flicked, and the page
 * after it took its place with nothing in between — a slideshow, which hides the two
 * things a reader wants from a page turn: the paper answering their hand, and some
 * sight of what is coming. Here the pages are laid out along an axis and dragged, so
 * the finger carries the page they are on out of the way and the next one in behind it,
 * and the reader has seen where they are going before they let go.
 *
 * The axis is the whole difference between the two layouts: which share of the reader a
 * slot is, which way the drag is measured, and which way the pages are drawn. Everything
 * else — the slot arithmetic, the snap, the slack at the ends, the door a forward drag
 * walks into — is the same view rotated, and deliberately not written twice.
 *
 * The hand has **two** gestures on the paper, and both are this view's, because both
 * move the paper: the pages can be *carried* along their own run — the run scrolls,
 * one page at a time, and the release settles on the page that is 60% visible — and
 * the paper can be *flicked across* that run, which is the page turn. The flick drags
 * the sheet under the finger exactly as the document card's pager does with its own
 * page, and lets it back the same way: eased, because a page that jumped back under
 * the hand would read as a gesture that failed rather than as an answer — and the
 * answer a forward flick most often gets is a page's *questions*, which is the case
 * the return exists for (`quizGate`). Which of the two gestures a touch has become is
 * `pageTouch`'s reading of it (`readPageDrag`), so the reader's root — which still
 * reads a flick where this view stands down — cannot disagree about one hand.
 *
 * What a drag *means* is `#/lib/document/snapPager`'s rule — how far it may carry the
 * pages, and which page a released drag is asking for — and what a turn is *allowed*
 * to do belongs to the parent: a drag towards the next page asks for the forward
 * *turn*, which is the door a page's questions hold (so `onSwipeNext` answers whether
 * the page actually moved), and a drag no more walks around those questions than the
 * arrow does. A refused turn puts the paper back where it was — smoothly, because a page
 * that jumped back under the hand would read as a gesture that failed rather than as a
 * question being asked.
 *
 * Two boundaries worth naming:
 *
 *  - **the paper is ours only while a page is a screenful.** At `zoom > 1` the page is
 *    larger than its slot and the reader is *reading* it — panning a line into view —
 *    so the pages grow to hold it, there is no snap position to land on, and the
 *    container scrolls freely until zoom is back at 1. A flick has nothing to turn at
 *    that size either, so only the carry is read;
 *  - **both of the page's gestures are claimed outright** (`touch-action`), so a
 *    finger's flick is the reader's rather than the browser's: panning is not left to
 *    the platform on either axis, because on one of them it *is* the page turn. Which
 *    axis the carry runs along follows the pages, and the other is the flick — the
 *    same division the document card's own pager draws the other way round.
 */
export function SnapReader({
  axis,
  doc,
  docKey,
  page,
  totalPages,
  ratio,
  zoom,
  dpr,
  size,
  locked,
  canStepForward,
  onSwipeNext,
  onSwipePrevious,
}: {
  /**
   * Which way the pages run: a column read downwards, or a row read across.
   *
   * The whole of this view is the same view rotated — one page to a slot, the paper
   * carried by the hand, a release settled on the page that is 60% visible — and the
   * axis is what the slot is a share of, which way the drag is read, and which way the
   * row of pages is laid out. It is the reader's own choice (`PdfFullscreen`), so the
   * pages are never laid out one way while the gestures expect the other.
   */
  axis: PageAxis
  doc: PDFDocumentProxy
  docKey: string
  page: number
  totalPages: number
  ratio: number
  zoom: number
  dpr: number
  size: {width: number; height: number}
  /**
   * Whether something other than the pages owns the surface — an open question card,
   * or the author's own editor.
   *
   * Nothing drags a page out from under either of them: a drag behind the card cannot
   * move the page the answers are being given to, and it cannot take the page the
   * author is writing on away from the card that is writing it. And while the author's
   * editor is open the whole gesture stands down, the flick included — which is why the
   * reader's root keeps reading the flick in that state, where it ends where the arrows
   * do (the draft's own question first).
   */
  locked: boolean
  /**
   * Whether the *forward step* is available at all — the reader's own test for it, and
   * the one its arrow is drawn from.
   *
   * A flick forward asks for a step, so it has to be the same step the arrow offers: a
   * last page has none, and a flick on one has nothing to ask for rather than something
   * to be refused.
   */
  canStepForward: boolean
  /** A drag towards the page after this one, which is the *forward turn*. True when it moved. */
  onSwipeNext: () => boolean
  /** A drag back, which is the page already read. Never held up. */
  onSwipePrevious: () => boolean
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  /**
   * The run of pages, as one thing the flick can hold — and the only element in here
   * the flick moves.
   *
   * A flick travels *across* the pages, and a run of pages has no way to move that
   * way: it is laid out along its own axis, and the scroll has nothing sideways in it.
   * So the paper is moved as *paint* rather than as a position — the run keeps exactly
   * the place the scroll gives it, and this layer is drawn a little to one side of it
   * while the hand is deciding. Nothing else can read the difference: a transform is
   * not layout, so the slot arithmetic, the snap positions and the page the reader is
   * on are all untouched by it.
   */
  const layerRef = useRef<HTMLDivElement | null>(null)

  /*
   * The slot: how much of the reader one page owns. The whole of it, by the reader's
   * own rule — a page is what the reader came for, and half a screen of paper with the
   * next one's edge beside it is a page read through a letterbox.
   *
   * Every page gets the same slot, which is what makes the scroll positions
   * arithmetical: the *n*th page begins at the *n - 1*st slot's worth of pages plus the
   * slack at the start (`slotStart`), so a snap is a number rather than a measurement,
   * and a page taller than another cannot misalign the pages after it.
   */
  const slot = Math.max(axis === 'y' ? size.height : size.width, 0)
  /*
   * Snapping is a promise about a page filling a slot, so it stands down while the
   * paper is larger than the slot — a zoomed reader has nothing to snap between, and
   * forcing them back to a page boundary would fight the pan they are making.
   */
  /*
   * The two doors out of a page, kept at one identity each.
   *
   * Both are handed over by the reader whenever its own page, question or draft
   * state changes — which is to say on most turns — and both are used by gestures
   * registered over the whole surface (a wheel listener, a release). So they are
   * read through a ref and the callbacks below depend on the wrappers, and one
   * thing stays true that the reader relies on: registering these gestures is not
   * bookkeeping that repeats whenever the page number changes
   * (`useNonReactiveCallback`).
   */
  const swipeNext = useNonReactiveCallback(onSwipeNext)
  const swipePrevious = useNonReactiveCallback(onSwipePrevious)

  const snaps = zoom === 1
  const {pageWidth, pageHeight} = fitPagedPage({
    width: size.width,
    height: size.height,
    ratio,
    zoom,
  })
  /*
   * How far apart two pages begin: one slot, or — while the paper is larger than the
   * slot — enough of it to hold the page with air around it, so a zoomed reader can
   * pan to its own edges.
   */
  const stride = snaps
    ? slot
    : Math.max(
        slot,
        (axis === 'y' ? pageHeight : pageWidth) + ZOOMED_ROW_AIR * 2,
      )
  /*
   * The room the two ends of the pages keep for the paper to move *into*: a scroll
   * box cannot be dragged past its own content, so a gesture at the first or the last
   * page needs space to exist at all (see `edgeSlack`). Everything below is measured
   * from the start of that slack rather than from the start of the box, which is why
   * one helper owns the arithmetic.
   */
  const slack = snaps ? edgeSlack(stride) : 0

  /** Where a page's slot begins along the pages. */
  const slotStart = useCallback(
    (target: number) => slack + (target - 1) * stride,
    [stride, slack],
  )

  /**
   * Where the pages are scrolled to, and how to move them — along whichever axis they
   * run.
   *
   * Everything below is expressed in *offsets along the pages* rather than in `scrollTop`
   * or `scrollLeft`, which is what makes one drag rule serve both layouts: the pages'
   * offsets grow towards the pages after this one in a column and in a row alike.
   */
  const readScroll = useCallback(
    () =>
      axis === 'y'
        ? (scrollRef.current?.scrollTop ?? 0)
        : (scrollRef.current?.scrollLeft ?? 0),
    [axis],
  )
  const writeScroll = useCallback(
    (offset: number) => {
      const el = scrollRef.current
      if (!el) return
      if (axis === 'y') el.scrollTop = offset
      else el.scrollLeft = offset
    },
    [axis],
  )
  const easeScroll = useCallback(
    (offset: number) => {
      const el = scrollRef.current
      if (!el) return
      el.scrollTo(
        axis === 'y'
          ? {top: offset, behavior: 'smooth'}
          : {left: offset, behavior: 'smooth'},
      )
    },
    [axis],
  )

  /**
   * How far across itself the paper follows the hand.
   *
   * `snapPager`'s carry rule is written towards the *next* page, and a flick carries
   * the paper the other way round — the hand moves the sheet, and moving the sheet
   * towards the next page is a hand travelling backwards along the run — so the sign is
   * the flick's while the bound is the pages': no further than a page, and a *give*
   * rather than a move at either end of the document (`EDGE_GIVE`), which is how a flick
   * on the first page still answers the hand instead of looking broken.
   */
  const flickOffset = useCallback(
    (across: number) =>
      -dragOffset({raw: -across, slot: stride, page, totalPages}),
    [page, stride, totalPages],
  )

  /**
   * Hold the paper where the hand has carried it.
   *
   * Written straight onto the layer's own style rather than through state, which is what
   * the document card's pager does with the same gesture and for the same reason: a page
   * that follows a finger by re-rendering its canvas cannot keep up with the finger.
   *
   * A hand that catches the sheet while it is still easing home — which is what a flick
   * refused by a page's questions leaves behind, and half the reason that ease is slow
   * enough to catch — **keeps the transition it caught**, so the paper eases to the hand
   * rather than jumping under it: the browser is the only thing that knows where a
   * transition has got to, and the card's own `animateBack` leans on exactly that. After
   * the caught ease is over, the transition is taken off and the paper is the hand's
   * again, one to one. A jump here would be a full page wide, so it is worth the lag
   * the card also accepts.
   */
  const drawFlick = useCallback(
    (offset: number) => {
      const el = layerRef.current
      if (!el) return
      el.style.transform =
        offset === 0
          ? ''
          : axis === 'y'
            ? `translate3d(${offset}px, 0, 0)`
            : `translate3d(0, ${offset}px, 0)`
    },
    [axis],
  )

  /**
   * Whose return the layer is wearing — so only that return's own timer may end it.
   *
   * A number rather than a flag: a flick made inside another's 160ms takes the token
   * and leaves the older timer with nothing to do (see `easeFlick`).
   */
  const easeRef = useRef(0)

  /**
   * Let the paper back where the run says it belongs, easing.
   *
   * Eased for the same reason `easeScroll` is, and with the card's own 160ms: the
   * flick that this is mostly for is the one that turned out to be a page's *questions*
   * rather than a turn — the door held, the questions are open, and the paper owes the
   * hand an answer. A page that snapped back instantly would read as a gesture that had
   * failed rather than as a question being asked.
   */
  const easeFlick = useCallback(() => {
    const el = layerRef.current
    if (!el) return
    /*
     * Whose return this is. The transition has to be taken off again once it is over —
     * it belongs to this one animation, not to the layer — and only its own timer may
     * do it: a reader who flicked again inside the 160ms, or a hand that caught the
     * paper and let it go again, would otherwise have the *newer* return cut off
     * mid-flight by the older one's cleanup — the jump the caught transition above
     * exists to avoid.
     */
    const ease = ++easeRef.current
    el.style.transition = `transform ${FLICK_EASE_MS}ms ease-out`
    el.style.transform = ''
    window.setTimeout(() => {
      if (easeRef.current !== ease) return
      // Only ever the layer that was moved: the paper is this view's, and a remount
      // takes it with it.
      if (layerRef.current === el) el.style.transition = ''
    }, FLICK_EASE_MS + 20)
  }, [])

  /**
   * The drag in flight, and which of the page's two gestures it has turned out to be.
   *
   * A gesture is only a drag once it has moved past the slop, and *which* drag it is
   * follows the hand — `readPageDrag`'s reading, and the same slop `pageTouch` uses for
   * the rest: a touch that travels further along the run of pages is *carrying* the
   * paper (the run scrolls under it), and one that travels further across them is
   * *flicking* it (the paper is drawn aside and the page turns). Neither is taken
   * before it is clear, because a press that stays put is a tap — the reader's
   * furniture — and the paper would otherwise steal it.
   */
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    /** Where the pages were when the grab landed: the anchor a zoomed pan works from. */
    scrollAt: number
    /**
     * Where the paper already was when the grab landed, measured from the current
     * page's own snap position.
     *
     * Normally nought — a drag begins on a page, because every gesture ends on one —
     * but a hand that catches the paper *during* a snap should carry on from where it
     * is rather than have the page jump the rest of the way to the position it was
     * heading for. What the drag is measured from is therefore the paper's own
     * position, and what it is *bounded* by is still the page the reader is on.
     */
    anchor: number
    /**
     * Which gesture this touch is, once it has moved enough to be one.
     *
     * Null until then, and never revised after: a hand that started along the pages is
     * carrying them however it wanders afterwards, which is the only reading that does
     * not make the paper change its mind halfway through a gesture.
     */
    mode: PageDrag | null
  } | null>(null)

  /**
   * The page this container has already been placed on, so the first placement is a
   * position rather than an animation.
   *
   * The reader can open on any page — a file read before, a page a reader asked for,
   * the first page that asks something — and a container that opened at its own top
   * would show page 1 and then *scroll* to page 9, which looks like a glitch and is a
   * whole document of work for anyone watching. So the first placement after the first
   * measurement is instant, and every one after it eases.
   */
  const placedRef = useRef(false)

  /**
   * Put the reader on the page they are on, whoever moved them there.
   *
   * One effect rather than a scroll in each of the places that can move a page (the
   * arrows, the keys, the thumbnails, a snap) because where the reader *is* is a fact
   * about the reader, not about the control that moved them: a container that trusted
   * its own gestures to keep itself in step would drift the moment another control
   * turned a page, and the page on screen and the page in the bar would disagree.
   */
  useLayoutEffect(() => {
    if (slot <= 0) return
    const target = slotStart(page)
    if (!placedRef.current) {
      placedRef.current = true
      writeScroll(target)
      return
    }
    easeScroll(target)
  }, [easeScroll, page, slot, slotStart, writeScroll])

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      // The primary press only: a right click is a menu, not a page turn.
      if (event.button !== 0 || locked) return
      const offset = readScroll()
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        scrollAt: offset,
        anchor: offset - slotStart(page),
        mode: null,
      }
    },
    [locked, page, readScroll, slotStart],
  )

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current
      if (!drag || event.pointerId !== drag.pointerId) return
      const dx = event.clientX - drag.startX
      const dy = event.clientY - drag.startY
      if (drag.mode === null) {
        const claim = readPageDrag({dx, dy, paging: axis})
        if (claim === null) return
        drag.mode = claim
        /*
         * The capture is what keeps a fast drag alive past the edge of the reader: a
         * pointer that leaves the element otherwise stops sending moves, and the paper
         * would stop with it while the finger carried on.
         */
        try {
          event.currentTarget.setPointerCapture(event.pointerId)
        } catch {
          // Best effort: without it a drag that leaves the page simply ends early.
        }
      }
      event.preventDefault()
      /*
       * Dragged *forwards* is positive: a hand going up carries the pages a column, a
       * hand going left carries the pages a row, and both of those move the reader
       * towards the page after this one.
       */
      const travel =
        axis === 'y' ? drag.startY - event.clientY : drag.startX - event.clientX
      /*
       * A zoomed page scrolls freely under the hand — the reader is inspecting it, and
       * there is no page boundary to snap to — with one thing said about flicks: there is
       * no page boundary to *turn* at that size either, so only a carry is read and a
       * flick is left as the nothing it is today.
       */
      if (!snaps) {
        if (drag.mode === 'carry') writeScroll(drag.scrollAt + travel)
        return
      }
      if (drag.mode === 'carry') {
        /*
         * The rule: where the pages already were, plus the travel of the hand, bounded
         * to one page by `snapPager`.
         */
        const carried = dragOffset({
          raw: drag.anchor + travel,
          slot: stride,
          page,
          totalPages,
        })
        writeScroll(slotStart(page) + carried)
        return
      }
      /*
       * The flick, which moves the paper rather than the run: the sheet follows the
       * hand *across* the pages — the one direction a run of pages cannot travel of
       * itself — and the release below decides what it was asking for.
       */
      drawFlick(flickOffset(axis === 'y' ? dx : dy))
    },
    [
      axis,
      drawFlick,
      flickOffset,
      page,
      slotStart,
      snaps,
      stride,
      totalPages,
      writeScroll,
    ],
  )

  /**
   * Let go: the reader is settled on exactly one page.
   *
   * Four answers, and the page's own questions have the last word on the two a hand can
   * turn: a page the reader has not finished with cannot be left by a gesture any more
   * than by an arrow. Either way the paper ends up on a page boundary, which is the whole
   * promise of a paged reader: never between two pages — and a flick, which was carrying
   * the paper and not the run, ends up back on the page it belongs to whichever answer it
   * got.
   */
  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current
      dragRef.current = null
      if (!drag || event.pointerId !== drag.pointerId) return
      try {
        event.currentTarget.releasePointerCapture(event.pointerId)
      } catch {
        // Nothing captured, nothing to release.
      }
      // A tap: not the pages' gesture at all, and the reader's root reads it.
      if (drag.mode === null) return
      /*
       * A zoomed page is left where the hand let go: the reader put it there to read
       * something, and a page that jumped back to a boundary would take the line they
       * were looking at with it.
       */
      if (!snaps) return
      if (drag.mode === 'flick') {
        /*
         * The flick's own reading, and the same one the reader's root makes where the
         * pages stand down: a flick that travelled far enough across the pages, and
         * further across them than along, asks for the page next door. Forward is the
         * *door* rather than a page number incremented — `canStepForward` is the test the
         * forward arrow is drawn from, and `onSwipeNext` answers whether it moved — and
         * backwards is a page already read, which is why it is never held up.
         */
        const touch = readPageTouch({
          dx: event.clientX - drag.startX,
          dy: event.clientY - drag.startY,
          paging: axis,
        })
        if (touch === 'next' && canStepForward) swipeNext()
        else if (touch === 'previous' && page > 1) swipePrevious()
        /*
         * The paper comes back either way. When the page turned it is the *run* that
         * has moved — the effect above eases it to the page the reader is now on — and
         * the paper returns from under the hand while that happens, exactly as the
         * card's pager does; when the step turned out to be a page's questions, this
         * return *is* the answer, and it is the only thing the gesture gets.
         */
        easeFlick()
        return
      }
      const base = slotStart(page)
      const target = readSnap({offset: readScroll() - base, slot: stride})
      if (target === 'next' && swipeNext()) return
      if (target === 'previous' && swipePrevious()) return
      easeScroll(base)
    },
    [
      axis,
      canStepForward,
      easeFlick,
      easeScroll,
      page,
      readScroll,
      slotStart,
      snaps,
      stride,
      swipeNext,
      swipePrevious,
    ],
  )

  /**
   * A gesture the browser took away — a cancelled pointer turns nothing.
   *
   * The paper goes back where it was, because a cancelled drag is one the reader never
   * finished making: leaving it where the interruption caught it would leave them
   * between two pages (a carry) or with the run drawn aside for no reason (a flick),
   * which is the one state this view exists to prevent.
   */
  const cancelDrag = useCallback(() => {
    const drag = dragRef.current
    if (!drag) return
    dragRef.current = null
    if (drag.mode === 'flick') {
      easeFlick()
      return
    }
    easeScroll(slotStart(page))
  }, [easeFlick, easeScroll, page, slotStart])

  /**
   * The wheel, one page a gesture.
   *
   * A wheel left to itself would scroll the column and stop wherever the notch landed
   * — the container has no idea it is a paged reader — so it turns pages instead, in
   * the steps the arrows do (and through the same door), with the cooldown the card's
   * own pager uses between notches so that one trackpad gesture is one page. Ctrl or
   * ⌘ belongs to the reader's zoom, and a zoomed page scrolls freely.
   */
  const lastWheelRef = useRef(0)
  const onWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      if (event.ctrlKey || event.metaKey || !snaps || locked) return
      const delta = event.deltaY !== 0 ? event.deltaY : event.deltaX
      if (Math.abs(delta) < WHEEL_MIN_DELTA) return
      const now = Date.now()
      const cooling = now - lastWheelRef.current < WHEEL_COOLDOWN_MS
      lastWheelRef.current = now
      event.preventDefault()
      if (cooling) return
      if (delta > 0) swipeNext()
      else swipePrevious()
    },
    [locked, snaps, swipeNext, swipePrevious],
  )

  /*
   * Nothing is drawn until the area has been measured: every page's slot is a share of
   * it, and a column built on a zero would be a flash of pages the size of a line.
   */
  if (slot <= 0) {
    return (
      <div
        ref={scrollRef}
        className={PDF_SCROLL_AREA_CLASS}
        style={{position: 'relative', width: '100%', height: '100%'}}
      />
    )
  }

  return (
    <div
      ref={scrollRef}
      className={PDF_SCROLL_AREA_CLASS}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={cancelDrag}
      onWheel={onWheel}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        /*
         * The reader's own box, with the ends' slack *inside* it. Every other box here
         * is a react-native `View`, which is border-box; a raw `div` is not, so without
         * this the padding is added to a `width: 100%` rather than taken out of it — a
         * row of pages would be paged against a box wider than the reader, and the
         * visible page would sit off to one side of the window it is read in.
         */
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: axis === 'y' ? 'column' : 'row',
        /*
         * No panning is the browser's, and that is the whole point: the page's two
         * gestures are one *finger* each on the same paper, and on one of the two axes
         * panning *is* the page turn. Leaving that axis to the platform is how a finger's
         * flick got cancelled out from under the reader — the browser took the pan and
         * the turn never happened — so the reader claims both and a flick is dragged by
         * the page itself. `pinch-zoom` is the one thing still handed over: two fingers
         * are how a reader inspects a page, and they are not this gesture.
         *
         * `overscroll-behavior` is the other half of owning the gesture: without it a
         * drag at the end of the pages chains to whatever is behind the reader, and a
         * reader who has reached the last page would pull the page behind them
         * instead.
         */
        touchAction: 'pinch-zoom',
        overscrollBehavior: 'contain',
        /*
         * The run's own axis scrolls, and the other one only *clips*.
         *
         * The flick draws the paper across the run, and paper hanging outside the reader
         * has to be cut off at its edge rather than added to what the box can scroll: a
         * transformed child counts as scrollable overflow, so an `auto` there would grow
         * a scrollbar (and a place to pan) every time a hand flicked a page. Nothing is
         * lost by clipping it — a fitted page is exactly as wide as the reader on its own
         * axis, which is the whole of the rule above.
         */
        overflowY: axis === 'y' ? 'auto' : 'hidden',
        overflowX: axis === 'y' ? 'hidden' : 'auto',
        // The ends' slack, as space the pages really have (see above).
        paddingTop: axis === 'y' ? slack : 0,
        paddingBottom: axis === 'y' ? slack : 0,
        paddingLeft: axis === 'y' ? 0 : slack,
        paddingRight: axis === 'y' ? 0 : slack,
        // A drag is not a text selection, and a page dragged under the hand must not
        // take the cursor with it.
        userSelect: 'none',
      }}>
      {/*
        The run of pages, as one layer the flick can hold.

        It changes nothing about the layout — the pages are laid out along the same axis
        and every slot is still the same size — and that is deliberate: a flick moves the
        *painting* of the run, not its geometry, because the geometry is what tells this
        view which page the reader is on (`slotStart`), and a flick that moved a page's
        place would be a page turn of a different kind. `flexShrink: 0` is what keeps it
        as long as the document inside a box that has a definite size.
      */}
      <div
        ref={layerRef}
        style={{
          display: 'flex',
          flexDirection: axis === 'y' ? 'column' : 'row',
          flexShrink: 0,
        }}>
        {Array.from({length: totalPages}).map((_, index) => (
          <div
            key={index}
            style={{
              ...(axis === 'y' ? {height: stride} : {width: stride}),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
            <LazyPage
              doc={doc}
              docKey={docKey}
              pageNumber={index + 1}
              width={pageWidth}
              height={pageHeight}
              dpr={dpr}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
